// Estado compartido de Novedades: el JSON de notas (una lectura por sesión) y la última versión
// que el usuario vio. Lo leen el punto del menú (Layout) y la página. En memoria a propósito, no
// en redux-persist: un JSON viejo persistido rompería «una lectura por sesión».
//
//   idle ──load()──► loading ──ok──► ready
//                        └───falla──► error ──reload()──► loading
//
// No usa la instancia de axios de la API: este archivo vive junto al bundle (PUBLIC_URL), no en
// REACT_APP_API_HOST, y no lleva token.

import { useSyncExternalStore } from "react";
import { ReleaseNotesDTO } from "../../interfaces/Dtos/ReleaseNotesDTO";

export type LoadStatus = "idle" | "loading" | "ready" | "error";

interface State {
  status: LoadStatus;
  data: ReleaseNotesDTO | null;
  /** Última versión publicada que vio el usuario actual; undefined si no se ha leído. */
  seen: string | null | undefined;
  userLogin: string | null;
}

let state: State = { status: "idle", data: null, seen: undefined, userLogin: null };
const listeners = new Set<() => void>();
let inFlight: Promise<void> | null = null;

const emit = () => listeners.forEach((l) => l());
const setState = (patch: Partial<State>) => {
  state = { ...state, ...patch };
  emit();
};

export const SEEN_PREFIX = "novedades.vista.";
const seenKey = (userLogin: string) => `${SEEN_PREFIX}${userLogin}`;

function readSeen(userLogin: string): string | null {
  try {
    return window.localStorage.getItem(seenKey(userLogin));
  } catch {
    return null;
  }
}

/** Compara X.Y.Z; negativo si a < b. Ignora sufijos (-rc.1+sha). */
export function compareVersions(a: string, b: string): number {
  const pa = a.split(/[-+]/)[0].split(".").map(Number);
  const pb = b.split(/[-+]/)[0].split(".").map(Number);
  for (let i = 0; i < 3; i++) if ((pa[i] || 0) !== (pb[i] || 0)) return (pa[i] || 0) - (pb[i] || 0);
  return 0;
}

function isReleaseNotes(value: unknown): value is ReleaseNotesDTO {
  const v = value as ReleaseNotesDTO;
  return !!v && typeof v === "object" && typeof v.environment === "string" && Array.isArray(v.published);
}

export async function fetchReleaseNotes(): Promise<ReleaseNotesDTO> {
  const base = process.env.PUBLIC_URL || "";
  const response = await fetch(`${base}/release-notes.json?ts=${Date.now()}`, { cache: "no-store" });
  if (!response.ok) throw new Error(`release-notes.json respondió ${response.status}`);
  // Sin el archivo, el servidor devuelve index.html con 200 (fallback del SPA).
  const type = response.headers.get("content-type") || "";
  if (type.includes("text/html")) throw new Error("release-notes.json no existe (el servidor devolvió HTML)");
  const json: unknown = await response.json();
  if (!isReleaseNotes(json)) throw new Error("release-notes.json no tiene la forma esperada");
  return json;
}

/** Inicia la lectura si no se hizo en esta sesión. Idempotente. */
export function loadReleaseNotes(force = false): Promise<void> {
  if (inFlight) return inFlight;
  if (!force && (state.status === "ready" || state.status === "error")) return Promise.resolve();
  setState({ status: "loading" });
  inFlight = fetchReleaseNotes()
    .then((data) => setState({ status: "ready", data }))
    .catch((error) => {
      console.error("Novedades: no se pudo leer release-notes.json", error);
      setState({ status: "error", data: null });
    })
    .finally(() => {
      inFlight = null;
    });
  return inFlight;
}

/** Cambia de usuario (o lo quita al cerrar sesión): relee su última versión vista. */
export function setCurrentUser(userLogin: string | null) {
  if (userLogin === state.userLogin && state.seen !== undefined) return;
  setState({ userLogin, seen: userLogin ? readSeen(userLogin) : null });
}

/** Marca como vista la última versión publicada. Solo con datos cargados. */
export function markLatestSeen() {
  const latest = state.data?.published[0]?.version;
  if (state.status !== "ready" || !latest || !state.userLogin) return;
  if (state.seen && compareVersions(state.seen, latest) >= 0) return;
  try {
    window.localStorage.setItem(seenKey(state.userLogin), latest);
  } catch {
    // Sin localStorage no hay punto, y tampoco error.
  }
  setState({ seen: latest });
}

/** Hay punto: datos cargados, alguna versión publicada y la última no vista. */
export function hasUnread(s: Pick<State, "status" | "data" | "seen">): boolean {
  const latest = s.data?.published[0]?.version;
  if (s.status !== "ready" || !latest || s.seen === undefined) return false;
  return !s.seen || compareVersions(s.seen, latest) < 0;
}

/** Versiones publicadas posteriores a la última vista que piden algo al usuario. */
export function unreadWithAction(s: Pick<State, "data" | "seen">) {
  if (!s.data || s.seen === undefined) return [];
  return s.data.published.filter((v) => v.accion && (!s.seen || compareVersions(v.version, s.seen) > 0));
}

const subscribe = (l: () => void) => {
  listeners.add(l);
  return () => listeners.delete(l);
};
const getSnapshot = () => state;

export function useReleaseNotesState() {
  return useSyncExternalStore(subscribe, getSnapshot);
}

/** Solo para pruebas. */
export function resetReleaseNotesStore() {
  state = { status: "idle", data: null, seen: undefined, userLogin: null };
  inFlight = null;
  emit();
}
